import { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Search,
  MapPin,
  Users,
  Star,
  Shield,
  Heart,
  BookOpen,
  X,
  CheckCircle,
  Award,
  TrendingUp,
  Sparkles,
  Loader2,
  Plus,
  Minus,
} from "lucide-react";
import HostelDetailPage from "./HostelDetailPage";
import type { Hostel } from "./hostelData";
import { notify } from "@/components/common/Notification";
import ImageBg from "../../assets/hotels1.png";
import { SitePagination } from "../ui/SitePagination";
import {
  publicInstitutionsAPI,
  type InstitutionDetailItem,
  type InstitutionListItem,
} from "../../services/api";

const ITEMS_PER_PAGE = 6;

interface EducationalYearItem {
  total_capacity: number;
  vacant: number;
  occupied: number;
  educational_year: string;
}

interface DirectoryHostelItem extends Hostel {
  apiId: string;
  departments: string[];
  levels: string[];
  teachingStaff?: number;
  nonTeachingStaff?: number;
  classrooms?: number;
  trusteeCount?: number;
  contactPerson?: string;
  email?: string;
  phone?: string;
  website?: string;
  images?: string[];
  selectedFacilities?: string[];
  selected_facilities?: string[];
  educational_year?: EducationalYearItem[];
  selected_levels?: string[];
  boarding_gender?: string;
  campus_area?: string;
  trustee_contact?: string;
  trustee_email?: string;
  trust_registration?: string;
  other_trustees?: string;
  trustee_designation?: string;
  video?: string;
}

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(item => {
      if (typeof item === "string") {
        return item;
      }
      if (item && typeof item === "object") {
        const record = item as Record<string, unknown>;
        const label = record.label ?? record.name ?? record.value;
        return typeof label === "string" ? label : "";
      }
      return "";
    })
    .filter(Boolean);
};

const parseEducationalYear = (value: unknown): EducationalYearItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item: unknown) => {
      if (!item || typeof item !== "object") {
        return null;
      }
      const row = item as Record<string, unknown>;
      const total_capacity = Number(row.total_capacity);
      const occupied = Number(row.occupied ?? 0);
      const vacant = Number(row.vacant ?? row.vacant_oocupied ?? 0);
      const educational_year =
        typeof row.educational_year === "string"
          ? row.educational_year
          : String(row.educational_year ?? "");
      if (!educational_year) {
        return null;
      }
      return {
        total_capacity: Number.isFinite(total_capacity) ? total_capacity : 0,
        occupied: Number.isFinite(occupied) ? occupied : 0,
        vacant: Number.isFinite(vacant) ? vacant : 0,
        educational_year,
      };
    })
    .filter((x): x is EducationalYearItem => x !== null);
};

const isVideoUrl = (url?: string): boolean => {
  if (!url) {
    return false;
  }
  return (
    /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(url) ||
    url.includes("vimeo.com") ||
    url.includes("youtube.com") ||
    url.includes("youtu.be")
  );
};

const getInstitutionImage = (institution: InstitutionListItem): string => {
  if (typeof institution.banner === "string" && institution.banner.trim()) {
    return institution.banner;
  }

  const images = institution.images;
  if (Array.isArray(images) && typeof images[0] === "string" && images[0]) {
    return images[0];
  }

  return ImageBg;
};

const getInstitutionImages = (institution: InstitutionListItem): string[] => {
  const images = institution.images;
  if (!Array.isArray(images)) {
    return [];
  }

  return images.filter(
    image => typeof image === "string" && image.trim().length > 0
  ) as string[];
};

const mapInstitutionToDirectoryHostel = (
  institution: InstitutionListItem,
  index: number
): DirectoryHostelItem => {
  const idValue =
    typeof institution.id === "number"
      ? institution.id
      : Number(institution.id ?? index + 1);
  const safeId = Number.isFinite(idValue) ? idValue : index + 1;

  const facilities = toStringArray(
    institution.selected_facilities ?? institution.selectedFacilities
  );

  return {
    id: safeId,
    apiId: String(institution.id ?? safeId),
    name: String(
      institution.name ?? institution.institution_name ?? "Institution"
    ),
    address: String(
      institution.address ??
        [institution.address_line1, institution.address_line2]
          .filter(Boolean)
          .join(", ") ??
        [institution.city ?? institution.district, institution.state]
          .filter(Boolean)
          .join(", ")
    ),
    city: String(institution.city ?? institution.district ?? "Unknown"),
    state: String(institution.state ?? "Unknown"),
    type: String(institution.type ?? institution.institution_type ?? "N/A"),
    capacity: toNumber(institution.capacity ?? institution.total_students, 0),
    facilities,
    fees: "₹8,000/month",
    scholarship: Boolean(
      institution.scholarship_available ?? institution.scholarship
    ),
    sicRating: toNumber(institution.sic_rating, 0),
    image: getInstitutionImage(institution),
    trust: String(institution.trustee_name ?? "JBF Registered Trust"),
    safetyRating: 4.5,
    hygieneRating: 4.5,
    academicSupport: 4.5,
    culturalAlignment: 4.5,
    governance: 4.5,
    teachingStaff: toNumber(institution.teaching_staff, 0),
    nonTeachingStaff: toNumber(institution.non_teaching_staff, 0),
    classrooms: toNumber(institution.classrooms, 0),
    trusteeCount: toNumber(institution.trustee_count, 0),
    contactPerson:
      typeof institution.contact_person === "string"
        ? institution.contact_person
        : "",
    email:
      typeof institution.email === "string"
        ? institution.email
        : "contact@jbf.org",
    phone: typeof institution.phone === "string" ? institution.phone : "",
    website: typeof institution.website === "string" ? institution.website : "",
    images: getInstitutionImages(institution),
    selectedFacilities: facilities,
    selected_facilities: facilities,
    departments: toStringArray(
      institution.selected_departments ?? institution.selectedDepartments
    ),
    levels: toStringArray(
      institution.selected_levels ?? institution.selectedLevels
    ),
    selected_levels: Array.isArray(
      (institution as Record<string, unknown>).selected_levels
    )
      ? (
          (institution as Record<string, unknown>).selected_levels as string[]
        ).filter((s): s is string => typeof s === "string")
      : undefined,
    educational_year: parseEducationalYear(
      (institution as Record<string, unknown>).educational_year
    ),
    boarding_gender:
      typeof (institution as Record<string, unknown>).boarding_gender ===
      "string"
        ? ((institution as Record<string, unknown>).boarding_gender as string)
        : undefined,
    campus_area:
      typeof (institution as Record<string, unknown>).campus_area === "string"
        ? ((institution as Record<string, unknown>).campus_area as string)
        : undefined,
    trustee_contact:
      typeof (institution as Record<string, unknown>).trustee_contact ===
      "string"
        ? ((institution as Record<string, unknown>).trustee_contact as string)
        : undefined,
    trustee_email:
      typeof (institution as Record<string, unknown>).trustee_email === "string"
        ? ((institution as Record<string, unknown>).trustee_email as string)
        : undefined,
    trust_registration:
      typeof (institution as Record<string, unknown>).trust_registration ===
      "string"
        ? ((institution as Record<string, unknown>)
            .trust_registration as string)
        : undefined,
    other_trustees:
      typeof (institution as Record<string, unknown>).other_trustees ===
      "string"
        ? ((institution as Record<string, unknown>).other_trustees as string)
        : undefined,
    trustee_designation:
      typeof (institution as Record<string, unknown>).trustee_designation ===
      "string"
        ? ((institution as Record<string, unknown>)
            .trustee_designation as string)
        : undefined,
    video:
      typeof (institution as Record<string, unknown>).video === "string"
        ? ((institution as Record<string, unknown>).video as string)
        : typeof (institution as Record<string, unknown>).video_link ===
            "string"
          ? ((institution as Record<string, unknown>).video_link as string)
          : isVideoUrl(getInstitutionImage(institution))
            ? getInstitutionImage(institution)
            : undefined,
  };
};

const mapInstitutionDetailToHostel = (
  institution: InstitutionDetailItem,
  fallback: DirectoryHostelItem
): DirectoryHostelItem => {
  const mapped = mapInstitutionToDirectoryHostel(institution, fallback.id - 1);
  return {
    ...fallback,
    ...mapped,
    id: fallback.id,
    apiId: String(institution.id ?? fallback.apiId),
  };
};

export default function HostelDirectoryPage() {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedEducationLevels, setSelectedEducationLevels] = useState<
    string[]
  >([]);
  const [selectedGenders, setSelectedGenders] = useState<string[]>([]);
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [expandedFilters, setExpandedFilters] = useState<string[]>([]);
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);

  const [showDetailPage, setShowDetailPage] = useState(false);
  const [selectedHostel, setSelectedHostel] =
    useState<DirectoryHostelItem | null>(null);
  const [directoryHostels, setDirectoryHostels] = useState<
    DirectoryHostelItem[]
  >([]);
  const [isListLoading, setIsListLoading] = useState(true);
  const [institutionsListLoaded, setInstitutionsListLoaded] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState<number[]>(
    []
  );
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to list view when route changes or navbar links are clicked
  useEffect(() => {
    setShowDetailPage(false);
  }, [location]); // Fetch States on mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const states = await publicInstitutionsAPI.getStates();
        setAvailableStates(states);
      } catch (err) {
        console.error("Failed to fetch states:", err);
      }
    };
    void fetchStates();
  }, []);

  // Fetch Cities when States change
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const cities = await publicInstitutionsAPI.getCities(
          selectedStates.length === 1 ? selectedStates[0] : undefined
        );
        setAvailableCities(cities);

        // Clear selected cities that are no longer available
        setSelectedCities(prev => prev.filter(city => cities.includes(city)));
      } catch (err) {
        console.error("Failed to fetch cities:", err);
      }
    };
    void fetchCities();
  }, [selectedStates]);

  const loadInstitutions = useCallback(async () => {
    setIsListLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("_", Date.now().toString());

      if (searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }

      selectedStates.forEach(s => params.append("state", s));
      selectedCities.forEach(c => params.append("city", c));
      selectedTypes.forEach(t => params.append("type", t));
      selectedGenders.forEach(g => params.append("boarding_gender", g));
      selectedEducationLevels.forEach(l => params.append("selected_levels", l));
      selectedFacilities.forEach(f => params.append("selected_facilities", f));

      const institutions =
        await publicInstitutionsAPI.getInstitutionsList(params);
      if (institutions && institutions.length > 0) {
        setDirectoryHostels(
          institutions.map((institution, index) =>
            mapInstitutionToDirectoryHostel(institution, index)
          )
        );
      } else {
        setDirectoryHostels([]);
      }
    } catch (error) {
      console.error("Failed to load institutions list:", error);
      setDirectoryHostels([]);
    } finally {
      setIsListLoading(false);
      setInstitutionsListLoaded(true);
    }
  }, [
    searchQuery,
    selectedStates,
    selectedCities,
    selectedTypes,
    selectedGenders,
    selectedEducationLevels,
    selectedFacilities,
  ]);

  // Trigger load whenever filters change (with optional debounce for search)
  useEffect(() => {
    const timer = setTimeout(() => {
      void loadInstitutions();
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [loadInstitutions]);

  // Dynamic filter options based on fetched Data and onboarding form constants
  const filterOptions = useMemo(() => {
    return {
      states: availableStates,
      cities: availableCities,
      types: [
        { value: "school", label: "School" },
        { value: "college", label: "College" },
        { value: "hostel", label: "Hostel" },
        { value: "boarding", label: "Boarding" },
        { value: "university", label: "University" },
        { value: "polytechnic", label: "Polytechnic" },
        { value: "iti", label: "ITI" },
        { value: "coaching", label: "Coaching Center" },
        { value: "gurukul", label: "Gurukul" },
        { value: "pathshala", label: "Pathshala" },
      ],
      levels: [
        { value: "primary", label: "Primary (1-5)" },
        { value: "middle", label: "Middle (6-8)" },
        { value: "secondary", label: "Secondary (9-10)" },
        { value: "higher_secondary", label: "Higher Secondary (11-12)" },
        { value: "undergraduate", label: "Undergraduate" },
        { value: "postgraduate", label: "Postgraduate" },
        { value: "doctorate", label: "Doctorate" },
      ],
      facilities: [
        "Library",
        "Computer Lab",
        "Science Lab",
        "Smart Classroom",
        "Auditorium",
        "Playground",
        "Sports Complex",
        "Gymnasium",
        "Cafeteria",
        "Medical Room",
        "WiFi",
        "CCTV",
        "Transportation",
        "Hostel",
        "AC Classrooms",
        "Prayer Hall",
        "Art Room",
        "Music Room",
        "Language Lab",
        "Robotics Lab",
      ],
      genders: [
        { value: "boys", label: "Boys" },
        { value: "girls", label: "Girls" },
      ],
    };
  }, [availableStates, availableCities]);

  // Since we now filter server-side, filteredHostels just uses directoryHostels
  const filteredHostels = directoryHostels;

  const totalPages = Math.max(
    1,
    Math.ceil(filteredHostels.length / ITEMS_PER_PAGE)
  );
  const paginatedHostels = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredHostels.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredHostels, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    selectedStates,
    selectedCities,
    selectedTypes,
    selectedEducationLevels,
    selectedGenders,
    selectedFacilities,
  ]);

  const toggleFilterExpansion = (filterName: string) => {
    setExpandedFilters(prev =>
      prev.includes(filterName)
        ? prev.filter(f => f !== filterName)
        : [...prev, filterName]
    );
  };

  const resetFilters = () => {
    setSearchQuery("");
    setSelectedStates([]);
    setSelectedCities([]);
    setSelectedTypes([]);
    setSelectedEducationLevels([]);
    setSelectedGenders([]);
    setSelectedFacilities([]);
  };

  const toggleComparison = (hostelId: number) => {
    if (selectedForComparison.includes(hostelId)) {
      setSelectedForComparison(
        selectedForComparison.filter(id => id !== hostelId)
      );
    } else if (selectedForComparison.length < 3) {
      setSelectedForComparison([...selectedForComparison, hostelId]);
    }
  };

  const comparisonHostels = directoryHostels.filter(h =>
    selectedForComparison.includes(h.id)
  );

  const handleViewDetails = async (hostel: DirectoryHostelItem) => {
    try {
      setIsDetailLoading(true);
      const detail = await publicInstitutionsAPI.getInstitutionDetail(
        hostel.apiId
      );
      setSelectedHostel(mapInstitutionDetailToHostel(detail, hostel));
      setShowDetailPage(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to load institution detail:", error);
      notify.error(
        "Unable to load details",
        "Please try again after some time."
      );
    } finally {
      setIsDetailLoading(false);
    }
  };

  if (showDetailPage && selectedHostel) {
    return (
      <HostelDetailPage
        hostel={selectedHostel}
        onBack={() => {
          setShowDetailPage(false);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex min-h-[70vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ImageBg}
            alt="Hostel accommodation"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/70 to-[#8B2E2E]/60" />
        </div>

        <div className="relative z-10 mx-auto mb-10 mt-10 max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-5xl lg:text-6xl">
              Institution Directory & Student Search
            </h1>
            <p className="text-2xl leading-relaxed text-gray-100">
              Find the perfect Jain hostel for your educational journey. Search,
              compare, and connect with verified institutions across India.
            </p>
          </div>
        </div>
      </section>

      {/* Hostel Directory Section with Sidebar */}
      <section className="bg-[#FFF9F0] py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row">
            {/* Left Sidebar Filters */}
            <div className="w-full shrink-0 lg:w-[320px]">
              <div className="sticky top-40 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                <div className="mb-4">
                  <h3 className="mb-3 text-[22px] font-semibold text-[#7A1E1E]">
                    Filters For Institution
                  </h3>
                  <button
                    onClick={resetFilters}
                    className="pointer-events-auto text-[15px] text-[#D4A017] underline decoration-1 underline-offset-4 hover:text-[#B8900F]"
                  >
                    Reset Filters
                  </button>
                  <div className="mt-4 h-[1px] w-full bg-gray-200" />
                </div>

                <div className="mb-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search Name or City..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full rounded border border-gray-300 py-2 pl-9 pr-4 text-[15px] focus:border-transparent focus:ring-1 focus:ring-[#7A1E1E]"
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  {/* Common styles for Accordions */}
                  {[
                    {
                      title: "Institution Type",
                      options: filterOptions.types,
                      selected: selectedTypes,
                      setter: setSelectedTypes,
                    },
                    {
                      title: "State selection",
                      options: filterOptions.states,
                      selected: selectedStates,
                      setter: setSelectedStates,
                    },
                    {
                      title: "City Selection Based On State",
                      options: filterOptions.cities,
                      selected: selectedCities,
                      setter: setSelectedCities,
                    },
                    {
                      title: "Education Level",
                      options: filterOptions.levels,
                      selected: selectedEducationLevels,
                      setter: setSelectedEducationLevels,
                    },
                    {
                      title: "Gender",
                      options: filterOptions.genders,
                      selected: selectedGenders,
                      setter: setSelectedGenders,
                    },
                    {
                      title: "Facilities",
                      options: filterOptions.facilities,
                      selected: selectedFacilities,
                      setter: setSelectedFacilities,
                    },
                  ].map(({ title, options, selected, setter }) => (
                    <div key={title} className="border-b border-gray-100 py-3">
                      <button
                        onClick={() => toggleFilterExpansion(title)}
                        className="flex w-full items-center justify-between text-left font-semibold text-gray-900 hover:text-[#7A1E1E]"
                      >
                        <span className="text-[16px]">{title}</span>
                        {expandedFilters.includes(title) ? (
                          <Minus className="h-4 w-4 text-[#7A1E1E]" />
                        ) : (
                          <Plus className="h-4 w-4 text-[#7A1E1E]" />
                        )}
                      </button>
                      {expandedFilters.includes(title) &&
                        options.length > 0 && (
                          <div className="mt-3 flex max-h-48 flex-col gap-2 overflow-y-auto pr-1">
                            {options.map((option: any) => {
                              const val =
                                typeof option === "string"
                                  ? option
                                  : option.value;
                              const label =
                                typeof option === "string"
                                  ? option
                                  : option.label;
                              return (
                                <label
                                  key={val}
                                  className="flex cursor-pointer items-start gap-3 py-1"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selected.includes(val)}
                                    onChange={() => {
                                      setter((prev: string[]) =>
                                        prev.includes(val)
                                          ? prev.filter(
                                              (v: string) => v !== val
                                            )
                                          : [...prev, val]
                                      );
                                    }}
                                    className="mt-[2px] h-[18px] w-[18px] rounded-sm border-gray-400 text-[#7A1E1E] focus:ring-[#7A1E1E]"
                                  />
                                  <span className="text-[14px] leading-tight text-gray-700">
                                    {label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      {expandedFilters.includes(title) &&
                        options.length === 0 && (
                          <div className="mt-3 text-[14px] italic text-gray-500">
                            No options available
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Content - Cards Grid */}
            <div className="flex-1">
              <div className="mb-8">
                <h2 className="mb-2 text-3xl text-[#7A1E1E]">
                  National Institution Directory
                </h2>
                <p className="text-gray-700">
                  {isListLoading
                    ? "Loading institutions..."
                    : `Found ${filteredHostels.length} Institutions`}
                </p>
              </div>

              {isListLoading ? (
                <div className="flex min-h-[320px] flex-col items-center justify-center py-16">
                  <Loader2 className="h-12 w-12 animate-spin text-[#7A1E1E]" />
                  <p className="mt-4 text-gray-600">Loading institutions...</p>
                </div>
              ) : institutionsListLoaded && directoryHostels.length === 0 ? (
                <div className="py-16 text-center">
                  <p className="text-lg text-gray-700">
                    No institutions available at the moment
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {paginatedHostels.map(hostel => (
                    <div
                      key={hostel.id}
                      className={`overflow-hidden rounded-xl bg-white shadow-lg transition-all hover:shadow-2xl ${
                        selectedForComparison.includes(hostel.id)
                          ? "ring-4 ring-[#D4A017]"
                          : ""
                      }`}
                    >
                      <div className="relative h-48">
                        {hostel.video ? (
                          isVideoUrl(hostel.video) &&
                          (hostel.video.includes("youtube.com") ||
                            hostel.video.includes("youtu.be") ||
                            hostel.video.includes("vimeo.com")) ? (
                            <iframe
                              title={hostel.name}
                              src={
                                hostel.video.includes("youtube.com") ||
                                hostel.video.includes("youtu.be")
                                  ? `https://www.youtube.com/embed/${
                                      hostel.video.includes("v")
                                        ? hostel.video
                                            .split("v=")[1]
                                            ?.split("&")[0]
                                        : hostel.video.split("/").pop()
                                    }`
                                  : `https://player.vimeo.com/video/${hostel.video
                                      .split("/")
                                      .pop()}`
                              }
                              className="pointer-events-none h-full w-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              onMouseOver={e => {
                                (e.target as HTMLIFrameElement).src +=
                                  "&autoplay=1&mute=1";
                              }}
                            />
                          ) : (
                            <video
                              src={hostel.video}
                              className="h-full w-full object-cover"
                              controls={false}
                              autoPlay
                              muted
                              loop
                              playsInline
                            />
                          )
                        ) : (
                          <img
                            src={hostel.image}
                            alt={hostel.name}
                            className="h-full w-full object-cover"
                          />
                        )}
                        <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-[#D4A017] px-3 py-1 text-white">
                          <Star className="h-4 w-4 fill-current" />
                          <span className="text-sm">{hostel.sicRating}</span>
                        </div>
                        {compareMode && (
                          <button
                            onClick={() => toggleComparison(hostel.id)}
                            className={`absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full ${
                              selectedForComparison.includes(hostel.id)
                                ? "bg-[#D4A017] text-white"
                                : "bg-white text-gray-700"
                            }`}
                          >
                            {selectedForComparison.includes(hostel.id) ? (
                              <CheckCircle className="h-5 w-5" />
                            ) : (
                              <div className="h-5 w-5 rounded-full border-2 border-gray-400" />
                            )}
                          </button>
                        )}
                      </div>

                      <div className="p-6">
                        <h3 className="mb-2 text-xl text-[#7A1E1E]">
                          {hostel.name}
                        </h3>

                        <div className="mb-4 space-y-2">
                          <div className="flex items-start gap-2 text-sm text-gray-700">
                            <MapPin className="mt-1 h-4 w-4 flex-shrink-0 text-[#D4A017]" />
                            <span className="text-[16px]">
                              {hostel.address}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Users className="h-4 w-4 text-[#D4A017]" />
                            <span className="text-[16px]">
                              Capacity: {hostel.capacity} | {hostel.type}
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            void handleViewDetails(hostel);
                          }}
                          disabled={isDetailLoading}
                          className="w-full rounded-lg bg-[#7A1E1E] py-3 text-white transition-all hover:bg-[#5A1515]"
                        >
                          {isDetailLoading ? "Loading..." : "View Details"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {institutionsListLoaded && directoryHostels.length > 0 && (
                <div className="mt-10">
                  <SitePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={filteredHostels.length}
                    pageSize={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                    itemLabel="hostels"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      {compareMode && selectedForComparison.length > 0 && (
        <section className="bg-white py-16">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
            <div className="mb-8 flex items-center justify-between">
              <h2 className="text-3xl text-[#7A1E1E]">
                Institution Comparison
              </h2>
              <button
                onClick={() => {
                  setSelectedForComparison([]);
                  setCompareMode(false);
                }}
                className="flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-white transition-all hover:bg-red-600"
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse overflow-hidden rounded-xl bg-white shadow-lg">
                <thead>
                  <tr className="bg-gradient-to-r from-[#7A1E1E] to-[#8B2E2E] text-white">
                    <th className="p-4 text-left">Attribute</th>
                    {comparisonHostels.map(hostel => (
                      <th key={hostel.id} className="p-4 text-center">
                        {hostel.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Location", key: "address" },
                    { label: "Capacity", key: "capacity" },
                    { label: "Type", key: "type" },
                    { label: "Fees", key: "fees" },
                    { label: "SIRC Rating", key: "sicRating", isRating: true },
                    {
                      label: "Safety Rating",
                      key: "safetyRating",
                      isRating: true,
                    },
                    {
                      label: "Hygiene Rating",
                      key: "hygieneRating",
                      isRating: true,
                    },
                    {
                      label: "Academic Support",
                      key: "academicSupport",
                      isRating: true,
                    },
                    {
                      label: "Cultural Alignment",
                      key: "culturalAlignment",
                      isRating: true,
                    },
                    { label: "Governance", key: "governance", isRating: true },
                  ].map((row, index) => (
                    <tr
                      key={row.label}
                      className={index % 2 === 0 ? "bg-[#FFF9F0]" : "bg-white"}
                    >
                      <td className="p-4 text-gray-800">{row.label}</td>
                      {comparisonHostels.map(hostel => {
                        const value =
                          hostel[row.key as keyof DirectoryHostelItem];
                        const display =
                          value === null || value === undefined
                            ? "—"
                            : Array.isArray(value)
                              ? value.every(
                                  (v): v is string => typeof v === "string"
                                )
                                ? value.join(", ")
                                : value.length > 0
                                  ? String(value.length) + " item(s)"
                                  : "—"
                              : typeof value === "object"
                                ? "—"
                                : String(value);
                        return (
                          <td key={hostel.id} className="p-4 text-center">
                            {row.isRating ? (
                              <div className="flex items-center justify-center gap-1">
                                <Star className="h-4 w-4 fill-[#D4A017] text-[#D4A017]" />
                                <span>{display}/5</span>
                              </div>
                            ) : (
                              <span>{display}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Hostel Comparison Criteria */}
      <section className="relative overflow-hidden bg-white py-16">
        {/* Background Decorative Elements */}
        <div className="absolute left-0 top-0 h-96 w-96 rounded-full bg-[#D4A017]/5 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-[#7A1E1E]/5 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
              Institution Comparison
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-lg text-gray-700">
              Compare hostels on key indicators to make an informed decision
            </p>
          </div>

          {/* Unique Alternating Layout */}
          <div className="space-y-12">
            {[
              {
                icon: <Shield className="h-10 w-10" />,
                title: "Safety",
                description:
                  "Security measures, CCTV coverage, safety protocols, emergency response systems",
                color: "maroon",
                position: "left",
              },
              {
                icon: <Sparkles className="h-10 w-10" />,
                title: "Hygiene",
                description:
                  "Cleanliness standards, sanitation facilities, regular maintenance, health compliance",
                color: "gold",
                position: "right",
              },
              {
                icon: <BookOpen className="h-10 w-10" />,
                title: "Academic Support",
                description:
                  "Study rooms, libraries, coaching facilities, mentorship programs",
                color: "maroon",
                position: "left",
              },
              {
                icon: <Heart className="h-10 w-10" />,
                title: "Welfare Indicators",
                description:
                  "Nutrition quality, healthcare access, recreational facilities, student wellbeing programs",
                color: "gold",
                position: "right",
              },
              {
                icon: <Users className="h-10 w-10" />,
                title: "Cultural Alignment",
                description:
                  "Jain values integration, religious practices support, cultural activities, community engagement",
                color: "maroon",
                position: "left",
              },
              {
                icon: <Award className="h-10 w-10" />,
                title: "Governance Transparency",
                description:
                  "S-ARDA compliance, transparent administration, accountability systems, student feedback mechanisms",
                color: "gold",
                position: "right",
              },
            ].map((criterion, index) => (
              <div
                key={criterion.title}
                className={`flex items-center gap-8 ${criterion.position === "right" ? "flex-row-reverse" : ""}`}
              >
                {/* Circular Icon Badge */}
                <div className="relative flex-shrink-0">
                  <div
                    className={`h-32 w-32 rounded-full ${
                      criterion.color === "maroon"
                        ? "bg-gradient-to-br from-[#7A1E1E] to-[#5A1515]"
                        : "bg-gradient-to-br from-[#D4A017] to-[#B8900F]"
                    } relative z-10 flex items-center justify-center text-white shadow-2xl`}
                  >
                    {criterion.icon}
                  </div>
                  {/* Connecting Line to Content */}
                  <div
                    className={`absolute top-1/2 ${
                      criterion.position === "right"
                        ? "right-0 translate-x-full"
                        : "left-0 -translate-x-full"
                    } h-0.5 w-8 bg-gradient-to-r ${
                      criterion.color === "maroon"
                        ? "from-[#7A1E1E]"
                        : "from-[#D4A017]"
                    } hidden md:block`}
                  />

                  {/* Number Badge */}
                  <div
                    className={`absolute -bottom-2 -right-2 h-10 w-10 rounded-full ${
                      criterion.color === "maroon"
                        ? "bg-[#D4A017]"
                        : "bg-[#7A1E1E]"
                    } z-20 flex items-center justify-center text-lg text-white shadow-lg`}
                  >
                    {index + 1}
                  </div>
                </div>

                {/* Content Card */}
                <div className={`group relative flex-1`}>
                  <div
                    className={`absolute inset-0 ${
                      criterion.color === "maroon"
                        ? "bg-gradient-to-br from-[#7A1E1E]/10 to-[#5A1515]/5"
                        : "bg-gradient-to-br from-[#D4A017]/10 to-[#B8900F]/5"
                    } transform rounded-2xl transition-transform duration-300 group-hover:scale-105`}
                  />

                  <div
                    className={`relative rounded-2xl bg-white p-8 shadow-lg ${
                      criterion.color === "maroon"
                        ? "border-l-4 border-r-4 border-l-[#7A1E1E] border-r-transparent"
                        : "border-l-4 border-r-4 border-l-transparent border-r-[#D4A017]"
                    } transition-all duration-300 group-hover:shadow-2xl`}
                  >
                    <h3
                      className={`mb-3 text-2xl ${
                        criterion.color === "maroon"
                          ? "text-[#7A1E1E]"
                          : "text-[#D4A017]"
                      }`}
                    >
                      {criterion.title}
                    </h3>
                    <p className="leading-relaxed text-gray-700">
                      {criterion.description}
                    </p>

                    {/* Decorative Corner Element */}
                    <div
                      className={`absolute ${
                        criterion.position === "right"
                          ? "left-4 top-4"
                          : "right-4 top-4"
                      } h-20 w-20 ${
                        criterion.color === "maroon"
                          ? "bg-[#7A1E1E]/5"
                          : "bg-[#D4A017]/5"
                      } -z-10 rounded-full`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="relative mt-16 overflow-hidden rounded-3xl bg-gradient-to-br from-[#7A1E1E] to-[#5A1515] p-10 text-center text-white shadow-2xl">
            {/* Decorative Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-[#D4A017] blur-2xl" />
              <div className="absolute bottom-0 left-0 h-40 w-40 rounded-full bg-white blur-2xl" />
            </div>

            <div className="relative z-10">
              <div className="mb-4 flex items-center justify-center gap-3">
                <TrendingUp className="h-8 w-8 text-[#D4A017]" />
                <h3 className="text-3xl">Make Data-Driven Decisions</h3>
              </div>
              <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-100">
                All hostels are rated on these criteria by independent auditors
                and student feedback, ensuring you have transparent, reliable
                information to choose the right hostel for your needs.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
